import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ButtonBase from '@mui/material/ButtonBase';
import GlobalStyles from '@mui/material/GlobalStyles';
import { type ReactNode, useRef, useState } from 'react';
import { preparePrintThumbnails } from './preparePrintThumbnails';

const PAGE_ASSET_TIMEOUT_MS = 5_000;

async function waitForPageAssets(): Promise<void> {
  const assets = Promise.all([
    document.fonts?.ready,
    ...Array.from(document.images, (image) => {
      if (image.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    }),
  ]);
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      assets,
      new Promise<void>((resolve) => {
        timeout = setTimeout(resolve, PAGE_ASSET_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export function PrintBaseStyles(): ReactNode {
  return (
    <GlobalStyles
      styles={(theme) => ({
        '@page': { size: 'A4 landscape', margin: '10mm' },
        '@media print': {
          'html, body, #root': {
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'visible',
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
          },
          body: {
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          },
          '*, *::before, *::after': { boxShadow: 'none !important' },
          video: { display: 'none !important' },
        },
      })}
    />
  );
}

// The browser names a "Save as PDF" file after the document title, so `title`
// stands in for it while the dialog is open.
export async function printPage(title?: string): Promise<void> {
  await waitForPageAssets();
  const restoreThumbnails = await preparePrintThumbnails();
  const pageTitle = document.title;
  if (title !== undefined) document.title = title;
  try {
    window.print();
  } finally {
    document.title = pageTitle;
    restoreThumbnails();
  }
}

export function PagePrintAction({ disabled = false }: { disabled?: boolean }): ReactNode {
  const isPreparingRef = useRef(false);
  const [isPreparing, setIsPreparing] = useState(false);

  async function handlePrint(): Promise<void> {
    if (isPreparingRef.current) return;
    isPreparingRef.current = true;
    setIsPreparing(true);
    try {
      await printPage();
    } finally {
      isPreparingRef.current = false;
      setIsPreparing(false);
    }
  }

  return (
    <>
      <PrintBaseStyles />
      <ButtonBase
        disabled={disabled || isPreparing}
        onClick={() => void handlePrint()}
        title="Export PDF"
        aria-label="Export PDF"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'border.strong',
          color: 'text.secondary',
          transition: 'box-shadow .15s, color .15s',
          '&:hover': { color: 'text.primary', boxShadow: 4 },
          '&.Mui-disabled': { color: 'text.disabled', borderColor: 'divider' },
        }}
      >
        <FileDownloadOutlined sx={{ fontSize: 19 }} />
      </ButtonBase>
    </>
  );
}
